import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, X, Check, Plus, Shapes, Globe, Maximize2, Minimize2, ChevronLeft, ChevronRight } from 'lucide-react';
import { AVAILABLE_WIDGETS, AVAILABLE_SHORTCUTS } from '../../constants/presets';
import { getIconByName } from '../../constants/icons';
import '../../App.css';

const WidgetCard = ({ widget, isMaximized, pageData, currentPageId, handleAddWidget }) => {
  const [layoutIdx, setLayoutIdx] = useState(2);
  const [isHovered, setIsHovered] = useState(false);
  
  const layouts = [
    { w: 1, h: 1, label: '1x1' },
    { w: 2, h: 1, label: '2x1' },
    { w: 2, h: 2, label: '2x2' },
    { w: 4, h: 2, label: '4x2' }
  ];
  const currentLayout = layouts[layoutIdx];
  const Icon = widget.icon && typeof widget.icon !== 'string' ? widget.icon : getIconByName("AppWindow");
  const isAddedToPage = pageData[currentPageId]?.some(item => item.widgetType === widget.widgetType);
  
  const cardHeight = isMaximized ? '210px' : '260px';
  const getBoxDimensions = () => {
    const baseUnit = isMaximized ? 32 : 42;
    const gap = 6;
    return {
      width: currentLayout.w * baseUnit + (currentLayout.w - 1) * gap,
      height: currentLayout.h * baseUnit + (currentLayout.h - 1) * gap
    };
  };
  
  const dims = getBoxDimensions();
  const minDim = Math.min(dims.width, dims.height);
  const iconSize = Math.min(isMaximized ? 32 : 42, minDim * 0.55);

  return (
    <motion.div className="widget-card-container"
      whileHover={{ scale: 1.02, zIndex: 10, boxShadow: '0 0 0 1px var(--accent-color, #3b82f6), 0 8px 24px var(--shadow-color, rgba(0,0,0,0.15))' }}
      transition={{ duration: 0.2 }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{ height: cardHeight, borderRadius: '20px', background: 'var(--card-bg-glass, rgba(255, 255, 255, 0.05))', backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)', border: '1px solid var(--border-color, rgba(255,255,255,0.08))', padding: isMaximized ? '16px' : '20px', display: 'flex', flexDirection: 'column', boxSizing: 'border-box', position: 'relative' }}
    >
      <div style={{ display: 'flex', justifyContent: 'center', position: 'relative' }}>
        <div style={{ fontWeight: 600, fontSize: isMaximized ? '0.95rem' : '1.15rem', textAlign: 'center', letterSpacing: '0.5px', color: 'var(--text-color, #fff)' }}>{widget.name}</div>
        {widget.tag && (
          <div style={{ position: 'absolute', right: 0, top: 0, fontSize: isMaximized ? '0.7rem' : '0.75rem', padding: isMaximized ? '2px 6px' : '4px 10px', background: 'var(--accent-light, rgba(59, 130, 246, 0.15))', color: 'var(--accent-color, #60a5fa)', borderRadius: '12px', fontWeight: 500 }}>
            {widget.tag}
          </div>
        )}
      </div>

      <div style={{ fontSize: isMaximized ? '0.75rem' : '0.85rem', opacity: 0.5, textAlign: 'center', marginTop: isMaximized ? '4px' : '6px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {widget.description || '暂无描述信息'}
      </div>

      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: isMaximized ? '10px' : '16px', marginBottom: isMaximized ? '10px' : '16px', position: 'relative' }}>
        
        <AnimatePresence>
          {isHovered && (
            <motion.div 
              initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 10 }}
              style={{ position: 'absolute', left: isMaximized ? -10 : -15, cursor: 'pointer', color: 'var(--text-muted, rgba(255,255,255,0.5))', zIndex: 2, padding: '10px' }}
              onClick={(e) => { e.stopPropagation(); setLayoutIdx((prev) => (prev - 1 + layouts.length) % layouts.length); }}
              whileHover={{ color: 'var(--text-color, #fff)', scale: 1.1 }}
            >
              <ChevronLeft size={isMaximized ? 20 : 24} />
            </motion.div>
          )}
        </AnimatePresence>

        <motion.div 
          animate={{ width: dims.width, height: dims.height }}
          transition={{ type: 'spring', stiffness: 300, damping: 25 }}
          style={{ background: 'var(--card-bg, rgba(255,255,255,0.04))', borderRadius: isMaximized ? '12px' : '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: 'inset 0 1px 2px var(--border-color, rgba(255,255,255,0.05)), 0 8px 24px var(--shadow-color, rgba(0,0,0,0.1))', border: '1px solid var(--border-color, rgba(255,255,255,0.03))', position: 'relative', overflow: 'hidden' }}
        >
          <Icon size={iconSize} color="var(--text-color, #fff)" style={{ opacity: 0.8 }} />
        </motion.div>

        <AnimatePresence>
          {isHovered && (
            <motion.div 
              initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -10 }}
              style={{ position: 'absolute', right: isMaximized ? -10 : -15, cursor: 'pointer', color: 'var(--text-muted, rgba(255,255,255,0.5))', zIndex: 2, padding: '10px' }}
              onClick={(e) => { e.stopPropagation(); setLayoutIdx((prev) => (prev + 1) % layouts.length); }}
              whileHover={{ color: 'var(--text-color, #fff)', scale: 1.1 }}
            >
              <ChevronRight size={isMaximized ? 20 : 24} />
            </motion.div>
          )}
        </AnimatePresence>

        <div style={{ position: 'absolute', bottom: isMaximized ? -12 : -18, display: 'flex', gap: '6px', opacity: isHovered ? 1 : 0, transition: 'opacity 0.2s' }}>
          {layouts.map((_, idx) => (
            <div key={idx} style={{ width: 5, height: 5, borderRadius: '50%', background: idx === layoutIdx ? 'var(--text-color, #fff)' : 'var(--border-color, rgba(255,255,255,0.2))', transition: 'background 0.3s' }} />
          ))}
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 'auto' }}>
        <div style={{ fontSize: isMaximized ? '0.7rem' : '0.8rem', opacity: 0.5, background: 'var(--bg-hover, rgba(255,255,255,0.08))', padding: isMaximized ? '2px 6px' : '4px 10px', borderRadius: '12px', color: 'var(--text-color)' }}>
          {currentLayout.label}
        </div>
        
        <div 
          title="添加" 
          style={{ padding: isMaximized ? '4px 12px' : '6px 16px', background: isAddedToPage ? 'var(--bg-hover, rgba(255,255,255,0.05))' : 'var(--accent-color, #3b82f6)', color: isAddedToPage ? 'var(--text-muted, rgba(255,255,255,0.4))' : '#ffffff', borderRadius: '14px', fontSize: isMaximized ? '0.8rem' : '0.85rem', cursor: isAddedToPage ? 'not-allowed' : 'pointer', transition: 'all 0.2s', fontWeight: 500 }} 
          onClick={(e) => { 
            e.stopPropagation(); 
            if (!isAddedToPage) {
              handleAddWidget(widget, currentPageId, { w: currentLayout.w, h: currentLayout.h });
            }
          }}
          onMouseEnter={(e) => { if (!isAddedToPage) e.currentTarget.style.opacity = 0.85; }}
          onMouseLeave={(e) => { if (!isAddedToPage) e.currentTarget.style.opacity = 1; }}
        >
          {isAddedToPage ? '已添加' : '添加'}
        </div>
      </div>
    </motion.div>
  );
};

// Precalculate modal scale synchronously before rendering to avoid any resizing jump
export const calculateLibraryModalScale = (isMaximized) => {
  if (typeof window === 'undefined' || isMaximized) return 1;
  const availW = window.innerWidth * 0.94;
  const availH = window.innerHeight * 0.88;
  const baseW = 1240;
  const baseH = 760;
  const scaleW = availW / baseW;
  const scaleH = availH / baseH;
  const scale = Math.min(1, Math.min(scaleW, scaleH));
  return Math.max(0.65, Number(scale.toFixed(3)));
};

const LibraryModal = ({ activeTab, setActiveTab, onClose, menuItems, pageData, setPageData, dockItems, setDockItems, currentPageId, maxDockItems, source, onAddSearchEngine }) => {
  const [activeWidgetCategory, setActiveWidgetCategory] = useState(0);
  const [activeShortcutCategory, setActiveShortcutCategory] = useState(0);
  const [hoveredWidgetCategory, setHoveredWidgetCategory] = useState(null);
  const [hoveredShortcutCategory, setHoveredShortcutCategory] = useState(null);
  const [flippedCardId, setFlippedCardId] = useState(null);
  const [selectedSize, setSelectedSize] = useState({ w: 2, h: 2 });
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isMaximized, setIsMaximized] = useState(false);
  const [canMaximize, setCanMaximize] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  const showToast = (msg) => {
    setToastMessage(msg);
    setTimeout(() => {
      setToastMessage('');
    }, 2000);
  };

  const [customName, setCustomName] = useState('');
  const [customUrl, setCustomUrl] = useState('');
  const [customText, setCustomText] = useState('');
  const [customBgColor, setCustomBgColor] = useState('#EF4444');
  const [customIconType, setCustomIconType] = useState('text');
  const [customImage, setCustomImage] = useState(null);
  const customColors = ['#EF4444', '#F97316', '#F59E0B', '#84CC16', '#10B981', '#06B6D4', '#3B82F6', '#8B5CF6', '#713F12', '#BE185D'];

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      const token = localStorage.getItem('tab365_token');
      if (token) {
        const formData = new FormData();
        formData.append('file', file);
        try {
          const res = await fetch('/api/v1/sync/upload/icon', {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}` },
            body: formData
          });
          const data = await res.json();
          if (data.code === 200 && data.data) {
            setCustomImage(data.data);
            setCustomIconType('image');
          } else {
            showToast('图片上传失败: ' + (data.message || '未知错误'));
          }
        } catch (err) {
          console.error('Failed to upload image', err);
          showToast('图片上传失败，请检查网络');
        }
      } else {
        // Fallback to local base64 if not logged in
        const reader = new FileReader();
        reader.onloadend = () => {
          setCustomImage(reader.result);
          setCustomIconType('image');
        };
        reader.readAsDataURL(file);
      }
    }
  };

  const generateTextIcon = (text, bgColor) => {
    const canvas = document.createElement('canvas');
    canvas.width = 128;
    canvas.height = 128;
    const ctx = canvas.getContext('2d');
    
    ctx.fillStyle = bgColor;
    ctx.beginPath();
    ctx.roundRect(0, 0, 128, 128, 32);
    ctx.fill();
    
    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 44px sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(text.substring(0, 4), 64, 64);
    
    return canvas.toDataURL('image/png');
  };

  const handleSaveCustomShortcut = (closeAfter = true) => {
    if (!customUrl) {
      showToast('请输入网址');
      return;
    }
    const derivedText = customText || customName.substring(0, 4) || '导航';
    const finalIcon = customIconType === 'text' 
      ? generateTextIcon(derivedText, customBgColor)
      : customImage || generateTextIcon(derivedText, customBgColor);

    const newShortcut = {
      id: 'custom-' + Date.now(),
      name: customName || '自定义导航',
      url: customUrl.startsWith('http') ? customUrl : `https://${customUrl}`,
      icon: finalIcon,
      widgetType: 'custom',
      provider: '自定义'
    };

    if (source === 'dock') {
      handleAddDockItem(newShortcut);
    } else {
      handleAddShortcut(newShortcut, currentPageId);
    }

    if (closeAfter) {
      onClose();
    } else {
      setCustomName('');
      setCustomUrl('');
      setCustomText('');
      setCustomImage(null);
    }
  };

  const extendedWidgets = React.useMemo(() => [
    { 
      category: '全部', 
      items: AVAILABLE_WIDGETS.flatMap(c => {
         const items = c.items || c.shortcuts || [];
         return items.map(item => ({ ...item, _categoryName: c.category || c.name }));
      }) 
    },
    ...AVAILABLE_WIDGETS.map(c => ({
       ...c,
       items: (c.items || c.shortcuts || []).map(item => ({ ...item, _categoryName: c.category || c.name }))
    }))
  ], [AVAILABLE_WIDGETS]);

  const extendedShortcuts = React.useMemo(() => [
    { 
      category: '全部', 
      shortcuts: AVAILABLE_SHORTCUTS.flatMap(c => {
         const items = c.items || c.shortcuts || [];
         return items.map(item => ({ ...item, _categoryName: c.category || c.name }));
      }) 
    },
    ...AVAILABLE_SHORTCUTS.map(c => ({
       ...c,
       shortcuts: (c.items || c.shortcuts || []).map(item => ({ ...item, _categoryName: c.category || c.name }))
    }))
  ], [AVAILABLE_SHORTCUTS]);

  const [modalScale, setModalScale] = useState(() => calculateLibraryModalScale(isMaximized));

  React.useEffect(() => {
    setCanMaximize(true);
    const updateScale = () => {
      setModalScale(calculateLibraryModalScale(isMaximized));
    };
    window.addEventListener('resize', updateScale);
    return () => window.removeEventListener('resize', updateScale);
  }, [isMaximized]);

  const handleAddWidget = (widget, targetMenuId, size = {w: 2, h: 2}) => {
    setPageData(prev => {
      const newData = { ...prev };
      Object.keys(newData).forEach(menuId => {
        if (newData[menuId]) {
          newData[menuId] = newData[menuId].filter(item => item.widgetType !== widget.widgetType);
        }
      });
      const newPageItems = [...(newData[targetMenuId] || [])];
      const newWidget = { ...widget, id: `widget-${Date.now()}`, w: size.w, h: size.h };
      newPageItems.push(newWidget);
      newData[targetMenuId] = newPageItems;
      return newData;
    });
    setHasUnsavedChanges(true);
  };

  const handleAddShortcut = (shortcut, targetMenuId) => {
    if (source === 'searchDropdown') {
      if (onAddSearchEngine) onAddSearchEngine(shortcut);
      showToast('已添加到搜索引擎');
      return;
    }
    if (targetMenuId === null || targetMenuId === undefined) return;
    setPageData(prev => {
      const newData = { ...prev };
      const newPageItems = [...(newData[targetMenuId] || [])];
      const newShortcut = { 
        id: `shortcut-${Date.now()}`, 
        type: 'shortcut', 
        name: shortcut.name, 
        url: shortcut.url, 
        icon: shortcut.icon 
      };
      newPageItems.push(newShortcut);
      newData[targetMenuId] = newPageItems;
      return newData;
    });
    setHasUnsavedChanges(true);
  };

  const handleAddDockItem = (shortcut) => {
    if (!dockItems || !setDockItems) return;
    if (dockItems.length >= maxDockItems) {
      showToast(`快捷栏最多只能添加 ${maxDockItems} 个网站`);
      return;
    }
    setDockItems(prev => {
      if (prev.some(item => item.url === shortcut.url)) return prev;
      return [...prev, shortcut];
    });
  };

  const handleSizeChange = (widget, newSize) => {
    setSelectedSize(newSize);
    let existingMenuId = null;
    Object.keys(pageData).forEach(menuId => {
      if (pageData[menuId] && pageData[menuId].some(item => item.widgetType === widget.widgetType)) {
        existingMenuId = menuId;
      }
    });

    if (existingMenuId !== null) {
      setPageData(prev => {
        const newData = { ...prev };
        if (newData[existingMenuId]) {
          newData[existingMenuId] = newData[existingMenuId].map(item => {
            if (item.widgetType === widget.widgetType) {
              return { ...item, w: newSize.w, h: newSize.h };
            }
            return item;
          });
        }
        return newData;
      });
      setHasUnsavedChanges(true);
    } else {
      setHasUnsavedChanges(true);
    }
  };

  return (
    <div className="modal-overlay" style={{ zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <AnimatePresence>
        {toastMessage && (
          <motion.div
            initial={{ opacity: 0, y: -20, x: '-50%' }}
            animate={{ opacity: 1, y: 0, x: '-50%' }}
            exit={{ opacity: 0, y: -20, x: '-50%' }}
            style={{
              position: 'fixed',
              top: '40px',
              left: '50%',
              zIndex: 9999,
              background: 'rgba(0, 0, 0, 0.8)',
              color: 'var(--text-color, #fff)',
              padding: '10px 24px',
              borderRadius: '8px',
              fontSize: '14px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              backdropFilter: 'blur(10px)',
              border: '1px solid var(--border-color, rgba(255,255,255,0.1))'
            }}
          >
            {toastMessage}
          </motion.div>
        )}
      </AnimatePresence>
      <motion.div 
        initial={{ opacity: 0, scale: 0.98 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.98 }}
        transition={{ duration: 0.16, ease: [0.16, 1, 0.3, 1] }}
        className={`settings-modal ${isMaximized ? 'maximized' : ''}`}
        style={{ 
          width: isMaximized ? '100vw' : (source === 'dock' ? '1060px' : '1240px'), 
          maxWidth: isMaximized ? 'none' : '95vw',
          height: isMaximized ? '100vh' : '760px', 
          maxHeight: isMaximized ? 'none' : '92vh',
          zoom: isMaximized ? 1 : modalScale,
          border: '1px solid var(--border-color, rgba(255,255,255,0.1))', 
          borderRadius: isMaximized ? '0' : '24px', 
          background: 'transparent',
          transition: 'width 0.3s cubic-bezier(0.16, 1, 0.3, 1), height 0.3s cubic-bezier(0.16, 1, 0.3, 1), border-radius 0.3s'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {source !== 'dock' && (
          <div className="settings-sidebar" style={{ paddingTop: '46px' }}>
            <div className="settings-nav">
              <div className={`settings-nav-item ${activeTab === 'shortcut' ? 'active' : ''}`} onClick={() => setActiveTab('shortcut')}><Globe size={18}/> 导航库</div>
              <div className={`settings-nav-item ${activeTab === 'widget' ? 'active' : ''}`} onClick={() => setActiveTab('widget')}><Shapes size={18}/> 组件库</div>
              <div className={`settings-nav-item ${activeTab === 'custom' ? 'active' : ''}`} onClick={() => setActiveTab('custom')}><Plus size={18}/> 自定义图标</div>
            </div>
          </div>
        )}
          
          <div className="settings-content-wrapper" style={{ borderRadius: (source === 'dock' && !isMaximized) ? '24px' : undefined, overflow: 'hidden' }}>
            <div className="settings-controls">
              {canMaximize && (
                <div className="settings-maximize" onClick={() => setIsMaximized(!isMaximized)} title={isMaximized ? "还原" : "最大化"} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '6px', borderRadius: '6px', color: 'var(--text-secondary, rgba(255,255,255,0.7))', transition: 'all 0.2s' }} onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--text-color, #fff)'; e.currentTarget.style.background = 'var(--bg-hover, rgba(255,255,255,0.15))' }} onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--text-secondary, rgba(255,255,255,0.7))'; e.currentTarget.style.background = 'transparent' }}>
                  {isMaximized ? <Minimize2 size={18} /> : <Maximize2 size={18} />}
                </div>
              )}
              <div className="settings-close" onClick={onClose} title="关闭" style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '6px', borderRadius: '6px', color: 'var(--text-secondary, rgba(255,255,255,0.7))', transition: 'all 0.2s' }} onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--text-color, #fff)'; e.currentTarget.style.background = 'var(--bg-hover, rgba(255,255,255,0.15))' }} onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--text-secondary, rgba(255,255,255,0.7))'; e.currentTarget.style.background = 'transparent' }}>
                <X size={20} />
              </div>
            </div>

            <div className="settings-panel-content" style={{ display: 'flex', flexDirection: 'column', height: '100%', overflowY: 'hidden', padding: '30px 40px', boxSizing: 'border-box' }}>
              {activeTab !== 'custom' && (
                <div style={{ alignSelf: 'flex-start', marginTop: '16px', marginBottom: '20px', display: 'flex', alignItems: 'center', background: 'var(--bg-hover-light, rgba(255,255,255,0.05))', padding: '0 16px', borderRadius: '12px', width: '100%', maxWidth: '400px', height: '42px', border: '1px solid var(--border-color, rgba(255,255,255,0.1))', boxSizing: 'border-box' }}>
                  <Search size={18} color="var(--text-muted, rgba(255,255,255,0.4))" />
                  <input 
                    type="text" 
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder={(activeTab === 'widget' && source !== 'dock') ? "搜索组件..." : "搜索导航..."}
                    style={{ background: 'transparent', border: 'none', color: 'var(--text-color, #fff)', padding: '0 12px', width: '100%', outline: 'none', fontSize: '0.95rem' }}
                  />
                  {searchTerm && (
                    <div 
                      onClick={() => setSearchTerm('')}
                      style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', width: '24px', height: '24px', borderRadius: '50%', background: 'var(--bg-hover, rgba(255,255,255,0.1))' }}
                    >
                      <X size={14} color="var(--text-secondary, rgba(255,255,255,0.6))" />
                    </div>
                  )}
                </div>
              )}
          {(activeTab === 'widget' && source !== 'dock') && (
            <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginBottom: '20px', borderBottom: '1px solid rgba(255,255,255,0.1)', paddingBottom: '12px' }}>
                {extendedWidgets.map((category, idx) => (
                  <div 
                    key={idx}
                    onClick={() => setActiveWidgetCategory(idx)}
                    onMouseEnter={() => setHoveredWidgetCategory(idx)}
                    onMouseLeave={() => setHoveredWidgetCategory(null)}
                    style={{ 
                      cursor: 'pointer', padding: '6px 16px', borderRadius: '20px', fontSize: '0.95rem',
                      background: (activeWidgetCategory === idx) || (hoveredWidgetCategory === idx) ? 'var(--bg-hover, rgba(255,255,255,0.1))' : 'transparent',
                      color: (activeWidgetCategory === idx) || (hoveredWidgetCategory === idx) ? 'var(--text-color, #fff)' : 'var(--text-secondary, rgba(255,255,255,0.6))',
                      transition: 'all 0.2s', whiteSpace: 'nowrap'
                    }}
                  >
                    {category.category || category.name}
                  </div>
                ))}
              </div>

              <div className="widget-library custom-scrollbar hover-scrollbar" style={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden', marginLeft: '-12px', paddingLeft: '12px', marginRight: '-32px', paddingRight: '32px', boxSizing: 'border-box' }}>
                <div className="widget-grid" style={{ display: 'grid', gridTemplateColumns: isMaximized ? 'repeat(4, 1fr)' : 'repeat(2, 1fr)', gap: '24px', padding: '12px 12px 24px 12px' }}>
                  {extendedWidgets[activeWidgetCategory] && extendedWidgets[activeWidgetCategory].items
                    .filter(widget => !searchTerm || (widget.name || '').toLowerCase().includes(searchTerm.toLowerCase()))
                    .map((widget) => (
                      <WidgetCard 
                        key={widget.id} 
                        widget={widget} 
                        isMaximized={isMaximized} 
                        pageData={pageData} 
                        currentPageId={currentPageId} 
                        handleAddWidget={handleAddWidget} 
                      />
                    ))}
                </div>
              </div>
            </div>
          )}

          {(activeTab === 'shortcut' || source === 'dock') && (
            <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginBottom: '20px', borderBottom: '1px solid rgba(255,255,255,0.1)', paddingBottom: '12px' }}>
                {extendedShortcuts.map((category, idx) => (
                  <div 
                    key={idx} 
                    onClick={() => setActiveShortcutCategory(idx)} 
                    onMouseEnter={() => setHoveredShortcutCategory(idx)}
                    onMouseLeave={() => setHoveredShortcutCategory(null)}
                    style={{ 
                      cursor: 'pointer', padding: '6px 16px', borderRadius: '20px', fontSize: '0.95rem', 
                      background: (activeShortcutCategory === idx) || (hoveredShortcutCategory === idx) ? 'var(--bg-hover, rgba(255,255,255,0.1))' : 'transparent',
                      color: (activeShortcutCategory === idx) || (hoveredShortcutCategory === idx) ? 'var(--text-color, #fff)' : 'var(--text-secondary, rgba(255,255,255,0.6))', 
                      transition: 'all 0.2s', whiteSpace: 'nowrap' 
                    }}
                  >
                    {category.category || category.name}
                  </div>
                ))}
              </div>
              <div className="widget-library custom-scrollbar hover-scrollbar" style={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden', marginLeft: '-12px', paddingLeft: '12px', marginRight: '-32px', paddingRight: '32px', boxSizing: 'border-box' }}>
                <div className="widget-grid" style={{ display: 'grid', gridTemplateColumns: isMaximized ? 'repeat(auto-fill, minmax(280px, 1fr))' : 'repeat(3, minmax(0, 1fr))', gap: '20px', padding: '12px 12px 24px 12px', width: '100%', boxSizing: 'border-box' }}>
                  {extendedShortcuts[activeShortcutCategory] && (extendedShortcuts[activeShortcutCategory].items || extendedShortcuts[activeShortcutCategory].shortcuts || [])
                    .filter(shortcut => !searchTerm || (shortcut.name || '').toLowerCase().includes(searchTerm.toLowerCase()))
                    .map((shortcut) => {
                    return (
                      <motion.div key={shortcut.id} className="widget-card-container" whileHover={{ scale: 1.02, zIndex: 10, boxShadow: '0 0 0 1px var(--accent-color, #3b82f6), 0 4px 15px var(--shadow-color, rgba(0,0,0,0.15))' }} transition={{ duration: 0.2 }} style={{ height: '190px', borderRadius: '16px', minWidth: 0, boxSizing: 'border-box', position: 'relative' }}>
                        <div className="widget-card-front" style={{ background: 'var(--card-bg-glass, rgba(255, 255, 255, 0.06))', backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)', border: '1px solid var(--border-color, rgba(255,255,255,0.08))', borderRadius: '16px', padding: '20px', width: '100%', height: '100%', display: 'flex', flexDirection: 'column', gap: '12px', boxSizing: 'border-box' }}>
                          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                              <div style={{ width: '56px', height: '56px', background: 'rgba(255, 255, 255, 0.08)', backdropFilter: 'blur(10px)', border: '1px solid var(--border-color, rgba(255,255,255,0.1))', borderRadius: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-color, #fff)', boxShadow: '0 4px 16px rgba(0,0,0,0.1)', flexShrink: 0, overflow: 'hidden' }}>
                                <img src={shortcut.icon} alt={shortcut.name} style={{ width: '32px', height: '32px', objectFit: 'contain' }} />
                              </div>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', overflow: 'hidden' }}>
                                <div style={{ fontWeight: 600, fontSize: '1.1rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{shortcut.name}</div>
                                {shortcut.provider && <div style={{ fontSize: '0.8rem', opacity: 0.5 }}>{shortcut.provider}</div>}
                              </div>
                            </div>
                          </div>
                          {(() => {
                            const desc = shortcut.description || shortcut.desc || (shortcut.url ? shortcut.url : '暂无详细描述');
                            return (
                              <div title={desc} style={{ fontSize: '0.85rem', opacity: 0.7, lineHeight: 1.5, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', flex: 1, cursor: 'help', marginTop: '4px' }}>
                                {desc}
                              </div>
                            );
                          })()}
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 'auto' }}>
                            <div style={{ fontSize: '0.8rem', opacity: 0.5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '80px' }} title={shortcut._categoryName || ''}>
                              {shortcut._categoryName || ''}
                            </div>
                            <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
                              {(() => {
                                const isAddedToDock = dockItems?.some(item => item.url === shortcut.url);
                                const isAddedToPage = pageData[currentPageId]?.some(item => item.url === shortcut.url);
                                return (
                                  <>
                                    {source === 'searchDropdown' ? (
                                      <div 
                                        title="添加搜索引擎" 
                                        style={{ padding: '6px 12px', background: 'var(--bg-hover, rgba(255,255,255,0.1))', color: 'var(--text-color, #fff)', borderRadius: '14px', fontSize: '0.8rem', cursor: 'pointer', transition: 'all 0.2s', whiteSpace: 'nowrap' }} 
                                        onClick={(e) => { e.stopPropagation(); handleAddShortcut(shortcut, currentPageId); }}
                                        onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.2)'; }}
                                        onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.1)'; }}
                                      >
                                        添加搜索引擎
                                      </div>
                                    ) : (
                                      <>
                                        <div 
                                          title="添加" 
                                          style={{ padding: '6px 12px', background: isAddedToPage ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.1)', color: isAddedToPage ? 'var(--text-muted, rgba(255,255,255,0.4))' : 'var(--text-color, #fff)', borderRadius: '14px', fontSize: '0.8rem', cursor: isAddedToPage ? 'not-allowed' : 'pointer', transition: 'all 0.2s', whiteSpace: 'nowrap' }} 
                                          onClick={(e) => { e.stopPropagation(); if (!isAddedToPage) handleAddShortcut(shortcut, currentPageId); }}
                                          onMouseEnter={(e) => { if (!isAddedToPage) e.currentTarget.style.background = 'rgba(255,255,255,0.2)'; }}
                                          onMouseLeave={(e) => { if (!isAddedToPage) e.currentTarget.style.background = 'rgba(255,255,255,0.1)'; }}
                                        >
                                          {isAddedToPage ? '已添加' : '添加'}
                                        </div>
                                        <div 
                                          title="加入快捷栏" 
                                          style={{ padding: '6px 12px', background: isAddedToDock ? 'rgba(255,255,255,0.05)' : 'rgba(59, 130, 246, 0.2)', color: isAddedToDock ? 'var(--text-muted, rgba(255,255,255,0.4))' : '#3b82f6', borderRadius: '14px', fontSize: '0.8rem', cursor: isAddedToDock ? 'not-allowed' : 'pointer', transition: 'all 0.2s', whiteSpace: 'nowrap' }} 
                                          onClick={(e) => { e.stopPropagation(); if (!isAddedToDock) handleAddDockItem(shortcut); }}
                                          onMouseEnter={(e) => { if (!isAddedToDock) { e.currentTarget.style.background = '#3b82f6'; e.currentTarget.style.color = 'var(--text-color, #fff)'; } }}
                                          onMouseLeave={(e) => { if (!isAddedToDock) { e.currentTarget.style.background = 'rgba(59, 130, 246, 0.2)'; e.currentTarget.style.color = '#3b82f6'; } }}
                                        >
                                          {isAddedToDock ? '已添加' : '加入快捷栏'}
                                        </div>
                                      </>
                                    )}
                                  </>
                                );
                              })()}
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {(activeTab === 'custom' && source !== 'dock') && (
            <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, padding: '10px 20px', overflowY: 'auto' }}>
              <div style={{ fontSize: '1.2rem', fontWeight: 600, marginBottom: '24px' }}>添加自定义网址</div>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', maxWidth: '600px' }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <div style={{ width: '80px', fontSize: '0.95rem', opacity: 0.8 }}>名称</div>
                  <input type="text" value={customName} onChange={e => setCustomName(e.target.value)} placeholder="网站名称" style={{ flex: 1, background: 'var(--bg-hover-light, rgba(255,255,255,0.05))', border: '1px solid var(--border-color, rgba(255,255,255,0.1))', borderRadius: '8px', padding: '10px 16px', color: 'var(--text-color, #fff)', outline: 'none' }} />
                </div>
                
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <div style={{ width: '80px', fontSize: '0.95rem', opacity: 0.8 }}>网址</div>
                  <input type="text" value={customUrl} onChange={e => setCustomUrl(e.target.value)} placeholder="https://" style={{ flex: 1, background: 'var(--bg-hover-light, rgba(255,255,255,0.05))', border: '1px solid var(--border-color, rgba(255,255,255,0.1))', borderRadius: '8px', padding: '10px 16px', color: 'var(--text-color, #fff)', outline: 'none' }} />
                </div>
                
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <div style={{ width: '80px', fontSize: '0.95rem', opacity: 0.8 }}>文字</div>
                  <div style={{ flex: 1, position: 'relative' }}>
                    <input type="text" value={customText} onChange={e => setCustomText(e.target.value.substring(0, 4))} placeholder="最多4个字符" style={{ width: '100%', background: 'var(--bg-hover-light, rgba(255,255,255,0.05))', border: '1px solid var(--border-color, rgba(255,255,255,0.1))', borderRadius: '8px', padding: '10px 16px', color: 'var(--text-color, #fff)', outline: 'none', boxSizing: 'border-box' }} />
                    <div style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', fontSize: '0.8rem', opacity: 0.5 }}>{customText.length}/4</div>
                  </div>
                </div>
                
                <div style={{ display: 'flex', alignItems: 'center', marginTop: '10px' }}>
                  <div style={{ width: '80px', fontSize: '0.95rem', opacity: 0.8 }}>背景</div>
                  <div style={{ display: 'flex', gap: '12px' }}>
                    {customColors.map(color => (
                      <div key={color} onClick={() => setCustomBgColor(color)} style={{ width: '24px', height: '24px', borderRadius: '50%', background: color, cursor: 'pointer', border: customBgColor === color ? '2px solid #fff' : '2px solid transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s' }}>
                        {customBgColor === color && <Check size={14} color="var(--text-color, #fff)" />}
                      </div>
                    ))}
                  </div>
                </div>
                
                <div style={{ display: 'flex', alignItems: 'flex-start', marginTop: '10px' }}>
                  <div style={{ width: '80px', fontSize: '0.95rem', opacity: 0.8, paddingTop: '10px' }}>预览</div>
                  <div style={{ display: 'flex', gap: '24px' }}>
                    
                    {/* 文字图标 */}
                    <div onClick={() => setCustomIconType('text')} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px', cursor: 'pointer', opacity: customIconType === 'text' ? 1 : 0.5, transition: 'all 0.2s' }}>
                      <div style={{ width: '80px', height: '80px', borderRadius: '20px', background: customBgColor, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-color, #fff)', fontWeight: 'bold', fontSize: '1.8rem', border: customIconType === 'text' ? '2px solid #3b82f6' : '2px solid transparent', boxShadow: '0 4px 12px rgba(0,0,0,0.2)' }}>
                        {(customText || customName || '文字').substring(0, 4)}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.85rem' }}>
                        <div style={{ width: '14px', height: '14px', borderRadius: '50%', border: customIconType === 'text' ? '4px solid #3b82f6' : '1px solid rgba(255,255,255,0.4)', background: customIconType === 'text' ? '#fff' : 'transparent', boxSizing: 'border-box' }} />
                        文字图标
                      </div>
                    </div>
                    
                    {/* 本地上传 */}
                    <div onClick={() => { setCustomIconType('image'); document.getElementById('custom-icon-upload').click(); }} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px', cursor: 'pointer', opacity: customIconType === 'image' ? 1 : 0.5, transition: 'all 0.2s' }}>
                      <div style={{ width: '80px', height: '80px', borderRadius: '20px', background: 'var(--bg-hover-light, rgba(255,255,255,0.05))', display: 'flex', alignItems: 'center', justifyContent: 'center', border: customIconType === 'image' ? '2px solid #3b82f6' : '1px dashed rgba(255,255,255,0.2)', position: 'relative', overflow: 'hidden' }}>
                        {customImage ? <img src={customImage} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <Plus size={32} color="var(--text-muted, rgba(255,255,255,0.3))" />}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.85rem' }}>
                        <div style={{ width: '14px', height: '14px', borderRadius: '50%', border: customIconType === 'image' ? '4px solid #3b82f6' : '1px solid rgba(255,255,255,0.4)', background: customIconType === 'image' ? '#fff' : 'transparent', boxSizing: 'border-box' }} />
                        本地上传
                      </div>
                      <input id="custom-icon-upload" type="file" accept="image/*" onChange={handleImageUpload} style={{ display: 'none' }} />
                    </div>
                    
                  </div>
                </div>
                
                <div style={{ display: 'flex', gap: '16px', marginTop: '30px' }}>
                  <div onClick={() => handleSaveCustomShortcut(true)} style={{ width: '140px', textAlign: 'center', padding: '10px 0', background: '#3b82f6', color: 'var(--text-color, #fff)', borderRadius: '8px', cursor: 'pointer', fontWeight: 500, transition: 'all 0.2s' }} onMouseEnter={e => e.currentTarget.style.background = '#2563eb'} onMouseLeave={e => e.currentTarget.style.background = '#3b82f6'}>
                    保存
                  </div>
                  <div onClick={() => handleSaveCustomShortcut(false)} style={{ width: '140px', textAlign: 'center', padding: '10px 0', background: '#fff', color: '#18181c', borderRadius: '8px', cursor: 'pointer', fontWeight: 500, transition: 'all 0.2s' }} onMouseEnter={e => e.currentTarget.style.background = '#f3f4f6'} onMouseLeave={e => e.currentTarget.style.background = '#fff'}>
                    保存并继续
                  </div>
                </div>
                
              </div>
            </div>
          )}
            </div>
          </div>
      </motion.div>
    </div>
  );
};

export default LibraryModal;
